import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility,
  VisibilityOff,
  Person as PersonIcon,
  Lock as LockIcon,
  AccountCircle as AccountCircleIcon
} from '@mui/icons-material';
import { API_BASE_URL } from '../../api/index';
import { getToken, getUser, setUser } from '../../api/auth';

export const ProfileDialog = ({ open, onClose, currentUser }) => {
  const [formData, setFormData] = useState({
    nombre: currentUser?.name || '',
    usuario: currentUser?.email || '',
    password: '',
    confirmPassword: ''
  });
  
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Update form data when currentUser changes
  React.useEffect(() => {
    if (currentUser) {
      setFormData({
        nombre: currentUser?.name || '',
        usuario: currentUser?.email || '',
        password: '',
        confirmPassword: ''
      });
    }
  }, [currentUser]);

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Limpiar errores cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    }

    if (!formData.usuario.trim()) {
      newErrors.usuario = 'El usuario es requerido';
    }

    // Si se proporciona una contraseña, validarla
    if (formData.password) {
      if (formData.password.length < 6) {
        newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Las contraseñas no coinciden';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const token = getToken();
      const user = getUser();
      
      if (!token || !user) {
        throw new Error('No se encontró información de autenticación');
      }

      const updateData = {
        nombre: formData.nombre.trim(),
        usuario: formData.usuario.trim()
      };

      // Solo incluir contraseña si se proporciona
      if (formData.password) {
        updateData.password = formData.password;
      }

      const response = await fetch(`${API_BASE_URL}/api/usuarios/update-profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': user.id || currentUser?.id,
          'X-Company-ID': user.companyId || currentUser?.companyId || ''
        },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || 'Error al actualizar el perfil');
      }

      // Update stored user data
      const updatedUser = {
        ...user,
        name: updateData.nombre,
        email: updateData.usuario
      };
      setUser(updatedUser);

      setSuccessMessage('Perfil actualizado correctamente');
      
      // Cerrar el diálogo después de un breve delay
      setTimeout(() => {
        onClose();
        setSuccessMessage('');
        setFormData(prev => ({
          ...prev,
          password: '',
          confirmPassword: ''
        }));
      }, 2000);

    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setErrors({});
    setErrorMessage('');
    setSuccessMessage('');
    setFormData(prev => ({
      ...prev,
      password: '',
      confirmPassword: ''
    }));
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Editar Perfil
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box component="form" onSubmit={handleSubmit}>
          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 2 }}>
            {/* Campo Nombre */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 700 }}>
                Nombre Completo
              </Typography>
              <TextField
                value={formData.nombre}
                onChange={handleInputChange('nombre')}
                error={!!errors.nombre}
                helperText={errors.nombre}
                fullWidth
                variant="outlined"
                placeholder="Ingresa tu nombre completo"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccountCircleIcon color="action" />
                    </InputAdornment>
                  )
                }}
              />
            </Box>

            {/* Campo Usuario */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 700 }}>
                Nombre de Usuario
              </Typography>
              <TextField
                value={formData.usuario}
                onChange={handleInputChange('usuario')}
                error={!!errors.usuario}
                helperText={errors.usuario}
                fullWidth
                variant="outlined"
                placeholder="Ingresa tu nombre de usuario"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  )
                }}
              />
            </Box>

            {/* Campo Contraseña */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 700 }}>
                Nueva Contraseña (opcional)
              </Typography>
              <TextField
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange('password')}
                error={!!errors.password}
                helperText={errors.password || 'Deja vacío para mantener la contraseña actual'}
                fullWidth
                variant="outlined"
                placeholder="Ingresa una nueva contraseña"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Box>

            {/* Campo Confirmar Contraseña */}
            {formData.password && (
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 700 }}>
                  Confirmar Nueva Contraseña
                </Typography>
                <TextField
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleInputChange('confirmPassword')}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  fullWidth
                  variant="outlined"
                  placeholder="Confirma tu nueva contraseña"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={isLoading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
          color="primary"
          sx={{ fontWeight: "bold" }}
        >
          {isLoading ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

